/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';

import {
  Button,
  ButtonGroup,
  Chip,
  ChipGroup,
  Dialog,
  InlineNotification,
  LoadingSection,
  Typography2,
} from '@coursera/cds-core';

import { CSV_TEMPLATE_WITH_GROUP_URL } from 'bundles/enterprise-admin-constants/ProgramMembersConstants';
import AsyncCsvSubmissionModal from 'bundles/enterprise-admin-learners/components/invite-learners/AsyncCsvSubmissionModal';
import GroupAssignmentStep from 'bundles/enterprise-admin-learners/components/invite-learners/GroupAssignmentStep';
import IncompatibleContractGroupModal from 'bundles/enterprise-admin-learners/components/invite-learners/IncompatibleContractGroupModal';
import InviteOutcomeSummary from 'bundles/enterprise-admin-learners/components/invite-learners/InviteOutcomeSummary';
import { adaptCsvSyncResponse } from 'bundles/enterprise-admin-learners/components/invite-learners/adaptCsvSyncResponse';
import { resolveCsvGroupAssignment } from 'bundles/enterprise-admin-learners/components/invite-learners/resolveCsvGroupAssignment';
import { useContractScopedSubmit } from 'bundles/enterprise-admin-learners/hooks/useContractScopedSubmit';
import { useCsvInvitePipeline } from 'bundles/enterprise-admin-learners/hooks/useCsvInvitePipeline';
import type { InviteLearnersResult } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';
import ProgramInvitationsApiManager from 'bundles/enterprise-admin-program-users/utils/ProgramInvitationsApiManager';
import { FileUploadZone } from 'bundles/enterprise-ui/components/FileUploadZone';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
  `,
  notificationSpacing: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  uploadSection: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
};

export type CsvInviteModalProps = {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  /** Set when the group is already known (Group Details lock, single-group admin) — skips GroupAssignmentStep. */
  autoSelectedGroupId?: string;
  /** Set for multi-contract orgs once ContractSelectionStep has run — scopes the submission to that contract's roster. */
  selectedContractId?: string;
  /** Called after a successful synchronous import so callers (e.g. AllLearnersHeader) can refetch learner data. */
  onInvited: () => void;
};

/**
 * The backend routes each CSV row to its own group via the row's `group` column — a group
 * picked here only matters for authorization and where ungrouped rows land (see
 * `resolveCsvGroupAssignment`/`fallbackProgramId`). Org admins with no group known and no
 * Group column submit immediately and get a skippable follow-up step instead of a mandatory
 * picker, mirroring EmailInviteModal's `isOrgAdminNoGroup`.
 */
const CsvInviteModal = ({
  open,
  onClose,
  onBack,
  autoSelectedGroupId,
  selectedContractId,
  onInvited,
}: CsvInviteModalProps) => {
  const pipeline = useCsvInvitePipeline({ autoSelectedGroupId });
  // `pipeline.programId` is already the resolved upload target, so it doubles as the
  // submission fallback here rather than recomputing the same isGroupAdmin branch.
  const { reset, file, programId: fallbackProgramId, isGroupAdmin } = pipeline;
  const { manageableGroups, loading: manageableGroupsLoading } = useManageableGroups();

  const [isUploadNotificationDismissed, setIsUploadNotificationDismissed] = React.useState(false);
  const [csvSubmitError, setCsvSubmitError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [outcome, setOutcome] = React.useState<InviteLearnersResult | null>(null);
  const [showAsyncModal, setShowAsyncModal] = React.useState(false);
  const hasAutoSubmitted = React.useRef(false);

  // True once the org-level auto-submit succeeds — offers the optional post-send group step.
  const [showPostSendGroupStep, setShowPostSendGroupStep] = React.useState(false);
  // True only for the org-level auto-submit — a later group pick reuses this same submit
  // pipeline but must not re-offer the post-send step.
  const isOrgLevelInitialSubmit = React.useRef(false);

  const {
    requestSubmit: requestContractScopedSubmit,
    isResolvingRoster,
    showIncompatibleContractModal,
    dismissIncompatibleContractModal,
    reset: resetContractScopedSubmit,
  } = useContractScopedSubmit({ selectedContractId });

  const isAddToGroupFlow = Boolean(autoSelectedGroupId);
  const title = isAddToGroupFlow ? _t('Add learners using CSV') : _t('Invite learners using CSV');
  const processingLabel = isAddToGroupFlow ? _t('Adding your learners…') : _t('Processing your invites…');

  const decision = pipeline.preview
    ? resolveCsvGroupAssignment({ groupColumnState: pipeline.preview.groupColumnState, autoSelectedGroupId })
    : undefined;

  // Org admin, no group known, no CSV Group column — auto-submits under the System Program
  // below instead of the mandatory picker; mirrors EmailInviteModal's `isOrgAdminNoGroup`.
  const isOrgAdminNoGroupPick = !isGroupAdmin && decision !== undefined && !decision.skipGroupStep;

  const handleCsvSubmit = React.useCallback(
    async (groupId: string, rosterId?: string): Promise<void> => {
      if (!pipeline.preview) return;
      const isInitialOrgLevelSubmit = isOrgLevelInitialSubmit.current;
      isOrgLevelInitialSubmit.current = false;
      setCsvSubmitError('');
      setSubmitting(true);
      try {
        const apiManager = new ProgramInvitationsApiManager({
          program: { id: groupId },
          naptime: { executeMutation: () => undefined, refreshData: () => undefined },
        });
        const { s3File, shouldProcessAsync } = pipeline.preview;
        if (shouldProcessAsync) {
          const response = await apiManager.getProcessInvitationsAsynchronouslyPromise({
            s3Key: s3File.key,
            s3Bucket: s3File.bucket,
            rosterId,
            sendInvitations: true,
          });
          if (!response.ok) throw new Error(`Failed to start CSV import: ${response.statusText}`);
          setShowAsyncModal(true);
        } else {
          const response = await apiManager.getProcessInvitationsSynchronouslyPromise({
            s3Key: s3File.key,
            s3Bucket: s3File.bucket,
            rosterId,
            sendInvitations: true,
          });
          if (!response.ok) throw new Error(`Failed to process CSV import: ${response.statusText}`);
          // Only attribute the outcome to `groupId` when every row landed there — a CSV Group
          // column means rows may be split across groups the client doesn't know.
          const attributedProgramId = pipeline.preview.groupColumnState === 'ABSENT' ? groupId : undefined;
          const result = adaptCsvSyncResponse(await response.json(), attributedProgramId);
          setOutcome(result);
          // Only offer the post-send step when someone was actually invited/joined and the
          // admin has (or might still be loading) a group to offer.
          setShowPostSendGroupStep(
            isInitialOrgLevelSubmit &&
              result.successful.length + result.membershipEmails.length > 0 &&
              (manageableGroupsLoading || manageableGroups.length > 0)
          );
          onInvited();
        }
      } catch (err) {
        Sentry.captureException(err);
        setCsvSubmitError(_t('There was an error processing your CSV. Please try again.'));
      } finally {
        setSubmitting(false);
      }
    },
    [pipeline.preview, onInvited, manageableGroups.length, manageableGroupsLoading]
  );

  // Resolves the roster for `groupId`, then fires the submission (or the incompatible-contract modal).
  const requestSubmit = React.useCallback<(groupId: string) => void>(
    (groupId) => requestContractScopedSubmit(groupId, handleCsvSubmit),
    [requestContractScopedSubmit, handleCsvSubmit]
  );

  const handleGroupStepBack = () => {
    setCsvSubmitError('');
    hasAutoSubmitted.current = false;
    pipeline.reset();
  };

  const handleIncompatibleContractSkipPathClose = () => {
    dismissIncompatibleContractModal();
    handleGroupStepBack();
  };

  const handleSkipPostSendGroupStep = () => setShowPostSendGroupStep(false);

  React.useEffect(() => {
    if (!open) {
      reset();
      hasAutoSubmitted.current = false;
      isOrgLevelInitialSubmit.current = false;
      setCsvSubmitError('');
      setOutcome(null);
      setShowAsyncModal(false);
      setShowPostSendGroupStep(false);
      resetContractScopedSubmit();
    }
  }, [open, reset, resetContractScopedSubmit]);

  React.useEffect(() => {
    if (file) setIsUploadNotificationDismissed(false);
  }, [file]);

  React.useEffect(() => {
    if (
      pipeline.status === 'success' &&
      (decision?.skipGroupStep || isOrgAdminNoGroupPick) &&
      fallbackProgramId &&
      !hasAutoSubmitted.current
    ) {
      hasAutoSubmitted.current = true;
      isOrgLevelInitialSubmit.current = isOrgAdminNoGroupPick;
      requestSubmit(fallbackProgramId);
    }
  }, [pipeline.status, decision?.skipGroupStep, isOrgAdminNoGroupPick, fallbackProgramId, requestSubmit]);

  if (outcome && showPostSendGroupStep) {
    // CSV already submitted — this is now optional, not a submission gate.
    return (
      <React.Fragment>
        <GroupAssignmentStep
          open={open}
          // No back destination post-submission — both close and skip just dismiss this step.
          onBack={handleSkipPostSendGroupStep}
          onSubmit={requestSubmit}
          onSkip={handleSkipPostSendGroupStep}
          submitting={submitting || isResolvingRoster}
          errorMessage={csvSubmitError || undefined}
          selectedContractId={selectedContractId}
          skippable
        />
        <IncompatibleContractGroupModal
          open={showIncompatibleContractModal}
          onClose={dismissIncompatibleContractModal}
        />
      </React.Fragment>
    );
  }

  if (outcome) {
    return <InviteOutcomeSummary open={open} outcome={outcome} onClose={onClose} />;
  }

  if (showAsyncModal) {
    return <AsyncCsvSubmissionModal open={open} onClose={onClose} isAddToGroupFlow={isAddToGroupFlow} />;
  }

  if (pipeline.status === 'processing') {
    return (
      <Dialog open={open} onClose={() => {}} width="large">
        <HeadingGroup>{title}</HeadingGroup>
        <Content>
          <LoadingSection label={processingLabel} />
        </Content>
        <Actions />
      </Dialog>
    );
  }

  if (pipeline.status === 'success' && (decision?.skipGroupStep || isOrgAdminNoGroupPick)) {
    return (
      <React.Fragment>
        <Dialog open={open} onClose={() => {}} width="large">
          <HeadingGroup>{title}</HeadingGroup>
          <Content css={styles.content}>
            {!csvSubmitError && <LoadingSection label={processingLabel} />}
            {/* Stays mounted so screen readers catch the update. */}
            <div role="alert">
              {csvSubmitError && (
                <InlineNotification severity="error">
                  <Typography2 component="p">{csvSubmitError}</Typography2>
                </InlineNotification>
              )}
            </div>
          </Content>
          <Actions>
            {csvSubmitError && (
              <Button variant="secondary" onClick={handleGroupStepBack}>
                {_t('Back')}
              </Button>
            )}
          </Actions>
        </Dialog>
        <IncompatibleContractGroupModal
          open={showIncompatibleContractModal}
          // Group is auto-selected here, so send the admin back to the upload view to retry instead.
          onClose={handleIncompatibleContractSkipPathClose}
        />
      </React.Fragment>
    );
  }

  if (pipeline.status === 'success') {
    return (
      <React.Fragment>
        <GroupAssignmentStep
          open={open}
          onBack={handleGroupStepBack}
          onSubmit={requestSubmit}
          submitting={submitting || isResolvingRoster}
          errorMessage={csvSubmitError || undefined}
          selectedContractId={selectedContractId}
        />
        <IncompatibleContractGroupModal
          open={showIncompatibleContractModal}
          onClose={dismissIncompatibleContractModal}
        />
      </React.Fragment>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} width="large">
      <HeadingGroup supportText={_t('Upload a CSV with names, emails, and optional group or attribute details.')}>
        {title}
      </HeadingGroup>
      <Content css={styles.content}>
        {/* Stays mounted so screen readers catch the update. */}
        <div role="status">
          {file && !isUploadNotificationDismissed && (
            <InlineNotification
              severity="success"
              onDismiss={() => setIsUploadNotificationDismissed(true)}
              css={styles.notificationSpacing}
            >
              {_t('Learner data uploaded successfully.')}
            </InlineNotification>
          )}
        </div>

        {/* Stays mounted so screen readers catch the update. */}
        <div role="alert">
          {pipeline.status === 'error' && (
            <InlineNotification severity="error" css={styles.notificationSpacing}>
              {pipeline.error}
            </InlineNotification>
          )}
        </div>

        <InlineNotification
          css={styles.notificationSpacing}
          severity="information"
          title={_t('Download the CSV template')}
          inlineAction={
            <Button
              variant="ghost"
              component="a"
              href={CSV_TEMPLATE_WITH_GROUP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('Download')}
            </Button>
          }
        >
          {_t('Name and email required. Add groups or custom attributes to organise learners.')}
        </InlineNotification>

        <div css={styles.uploadSection}>
          <Typography2 component="p" variant="subtitleSmall">
            {_t('Upload the CSV')}
          </Typography2>

          <FileUploadZone
            layout="stacked"
            accept={{ 'text/csv': ['.csv'] }}
            singleFileOnly
            defaultMessage={_t('Click to upload or drag & drop')}
            defaultFileMessage={_t('CSV file only')}
            onDrop={({ files }) => {
              const [nextFile] = files;
              if (nextFile) pipeline.selectFile(nextFile);
            }}
          />

          {file && (
            <ChipGroup aria-label={_t('Uploaded file')} variant="delete">
              <Chip onClick={pipeline.clearFile}>{file.name}</Chip>
            </ChipGroup>
          )}
        </div>
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: isAddToGroupFlow ? _t('Add to group') : _t('Upload and invite'),
            disabled: !pipeline.file || !pipeline.programReady,
            onClick: pipeline.submit,
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: _t('Back'),
            onClick: onBack,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default CsvInviteModal;
